<template>
  <AppDeleteModal
    :keypath="confirmMessage.keyPath"
    :loading="isLoading"
    :show="show"
    :title="show ? confirmDeleteTitle : ''"
    data-auto-testid="okr-element-delete-modal"
    @on-close="hide"
    @on-confirm="deleteObjective"
  >
    <template #i18n-t:elementType> {{ confirmMessage.elementType }}</template>
    <template #i18n-t:elementName>
      <br v-if="objectiveIsTask || objectiveIsWorkItem" />
      <b> {{ objective.displayId }} {{ objective.name }} </b>
    </template>

    <template
      v-if="show && objectiveHasChildren && !objectiveIsTaskOrWorkItem"
      #app-dialog:footer-prepend
    >
      <AppCheckbox
        v-model="deleteNestedItems"
        class="odm-DeleteChildrenSwitch"
        data-auto-testid="delete-nested-items-switch"
      >
        {{ $t('confirm.objective.nested_items') }}
      </AppCheckbox>
    </template>

    <template #app-dialog:confirm-btn-text>
      {{ confirmBtnText }}
    </template>
  </AppDeleteModal>
</template>

<script>
import { defineComponent } from 'vue'

import ObjectivesApiHandler from '@/api/okr-elements'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { handleError } from '@/utils/error-handling'
import { showNotify } from '@/utils/notify'
import {
  isJiraIssueOrWorkItem,
  isKR,
  isWorkItem,
  OBJECTIVE_TYPES,
  objectiveIsJiraTask
} from '@/utils/objective-types'
import { getDeleteElementActionTranslationKey, getObjectiveTypeName } from '@/utils/objectives'
import { objectOrNullProp } from '@/utils/prop-validators'
import { getNavigationTabName } from '@/utils/tracking'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'

export default defineComponent({
  name: 'ObjectiveDeleteModal',

  components: {
    AppDeleteModal,
    AppCheckbox
  },

  props: {
    objective: {
      required: true,
      validator: v => objectOrNullProp(v)
    },

    show: {
      type: Boolean,
      default: false
    },

    source: {
      type: String,
      default: ''
    }
  },

  emits: { 'update:show': null, deleted: null },

  data() {
    return {
      deleteNestedItems: false,
      isLoading: false
    }
  },

  computed: {
    objectiveIsKR() {
      return isKR(this.objective)
    },

    objectiveIsTask() {
      return objectiveIsJiraTask(this.objective)
    },

    objectiveIsWorkItem() {
      return isWorkItem(this.objective)
    },

    objectiveIsTaskOrWorkItem() {
      return this.objectiveIsTask || this.objectiveIsWorkItem
    },

    confirmDeleteTitle() {
      const [title] = [
        this.objectiveIsKR && 'confirm.metric.title',
        this.objectiveIsTask &&
          getDeleteElementActionTranslationKey({
            [OKR_ELEMENT_KEYS.TYPE_ID]: OBJECTIVE_TYPES.TASK
          }),
        this.objectiveIsWorkItem &&
          getDeleteElementActionTranslationKey({
            [OKR_ELEMENT_KEYS.TYPE_ID]: OBJECTIVE_TYPES.WORK_ITEM
          }),
        'confirm.objective.title'
      ].filter(Boolean)

      return this.$t(title)
    },

    confirmMessage() {
      if (!this.show) {
        return {
          keyPath: ''
        }
      }

      const [keyPath] = [
        this.objectiveIsTask && 'delete_confirm_issue_note',
        this.objectiveIsWorkItem && 'delete_confirm_work_item_note',
        'delete_confirm_element_note'
      ].filter(Boolean)

      const [elementType] = [
        this.objectiveIsKR && 'okr_elements.metric',
        'okr_elements.objective'
      ].filter(Boolean)

      return {
        keyPath,
        elementType: this.$t(elementType).toLowerCase()
      }
    },

    confirmBtnText() {
      if (this.objectiveIsTask) {
        return this.$t('action.unlink_issue')
      }

      if (this.objectiveIsWorkItem) {
        return this.$t('action.unlink_work_item')
      }

      return this.$t('action.delete')
    },

    objectiveHasChildren() {
      return this.objective.childCount > 0
    }
  },

  methods: {
    async hide() {
      this.deleteNestedItems = false
      this.$emit('update:show', false)

      await this.$nextTick()
    },

    async deleteObjective() {
      const objectivesApi = new ObjectivesApiHandler()
      this.isLoading = true

      try {
        const elementId = this.objective.id
        await objectivesApi.removeOkrElement({
          elementId,
          removeNestedObjectives: this.deleteNestedItems
        })
        if (isJiraIssueOrWorkItem(this.objective)) {
          tracker.logEvent('Deleted link', {
            category: EVENT_CATEGORIES.OKR_MANAGEMENT,
            label: this.objective.issueType,
            source: this.source,
            event_ref: 'Plugin',
            contribution: this.objective.contribute ? 'Yes' : 'No',
            ent_id: this.objective.displayId,
            tab: getNavigationTabName(this.$route)
          })
        } else {
          tracker.logEvent(isKR(this.objective) ? 'Deleted KR' : 'Deleted objective', {
            category: EVENT_CATEGORIES.OKR_MANAGEMENT,
            source: this.source,
            label: getObjectiveTypeName(this.objective.typeId),
            contribution: this.objective.contribute ? 'Yes' : 'No',
            ent_id: this.objective.displayId,
            tab: getNavigationTabName(this.$route)
          })
        }

        showNotify({
          title: this.$t('bulk_actions.delete_notification', 1)
        })

        this.$emit('deleted', elementId)
        this.hide()
      } catch (error) {
        handleError({ error })
      } finally {
        this.isLoading = false
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.odm-DeleteChildrenSwitch {
  --checkmark-color: #{$grade-low-color-next};
  --label-color: #{$grade-low-color-next};
  &:deep(.ac-Text) {
    font-family: $system-ui;
  }
}
</style>
